import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { useIntegrations } from '@/hooks/useIntegrations';
import { useUserBusiness } from '@/hooks/useUserBusiness';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { useBusinessDiscountForManagement } from '@/hooks/useBusinessDiscount';
import { useBusinessSubscriptionStatus } from '@/hooks/useBusinessSubscriptionStatus';
import { supabase } from '@/integrations/supabase/client';
import { LayoutDashboard, Users, Store, Palette, Tag, LogOut, ChevronDown, Settings, Building, Home, Languages, CreditCard, Plug, ListTree, Mail, Star, FileText, BadgeCheck, Briefcase, MessagesSquare, LayoutList, Target, Sparkles } from 'lucide-react';
import { SidebarProvider, Sidebar, SidebarContent, SidebarHeader, SidebarMenu, SidebarFooter, SidebarGroup, SidebarTrigger } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { getUserInitials, hasAdminDashboardAccess } from '@/utils/userUtils';
import UpgradePromptButton from './UpgradePromptButton';

const AdminLayout = () => {
  const {
    user,
    signOut,
    userProfile
  } = useAuth();
  const {
    userRole,
    currentCommunity
  } = useCommunity();
  const {
    isStripeActive
  } = useIntegrations();
  const {
    isBusinessOwner,
    userBusinessId,
    isLoading: isBusinessLoading
  } = useBusiness();
  const {
    data: userBusiness
  } = useUserBusiness();
  const {
    isAllowedTo
  } = useCommunityMember();
  const { data: businessDiscount } = useBusinessDiscountForManagement(userBusinessId);
  const { data: subscriptionInfo } = useBusinessSubscriptionStatus(userBusinessId || '');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
    siteManagement: false,
    servicesManagement: false,
    myBusinessSection: false,
    userManagement: false
  });
  const [canManageFeatures, setCanManageFeatures] = useState(false);
  const [hasSmartJobMatching, setHasSmartJobMatching] = useState<boolean | null>(null);
  const [hasDirectMessaging, setHasDirectMessaging] = useState<boolean | null>(null);
  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
  const isReviewer = userRole === 'reviewer';
  const canManageBusiness = isAdmin || isReviewer;
  const canManageUsers = isAdmin;
  const canManageSiteSettings = isAdmin;
  const canManageTranslations = isAdmin;
  const hasDashboardAccess = isAdmin || isReviewer || isBusinessOwner;
  const hasActiveSubscription = subscriptionInfo?.hasActiveSubscription || false;
  const isBusinessApproved = userBusiness?.is_active === true;
  const isFreeBusinessType = businessDiscount?.type === 'Free';
  
  // Show subscription menu for businesses with active subscriptions (not free businesses)
  const showSubscriptionMenu = isStripeActive && 
    userBusinessId && 
    userBusiness?.is_active === true && 
    businessDiscount?.type !== 'Free' && 
    hasActiveSubscription;
    
  // Show upgrade prompt for business owners who need to subscribe (not free businesses, no active subscription)
  const showUpgradePrompt = isBusinessOwner && 
    userBusinessId && 
    userBusiness?.is_active === true && 
    businessDiscount?.type !== 'Free' && 
    isStripeActive && 
    !hasActiveSubscription;

  // Check if user can manage features
  useEffect(() => {
    const checkFeaturePermission = async () => {
      if (isAdmin) {
        const allowed = await isAllowedTo('manage_features');
        setCanManageFeatures(allowed);
      }
    };
    checkFeaturePermission();
  }, [isAdmin, isAllowedTo]);

  // Check business features for subscription-gated menu items
  useEffect(() => {
    const checkBusinessFeatures = async () => {
      if (!userBusinessId) {
        return;
      }

      try {
        // Check smart job matching feature for all businesses (free and paid)
        const { data: hasJobMatching, error: jobError } = await supabase.rpc('has_feature', {
          business_id: userBusinessId,
          feature_key: 'smart_job_matching'
        });

        // Check direct messaging feature for all businesses (free and paid)
        const { data: hasMessaging, error: messagingError } = await supabase.rpc('has_feature', {
          business_id: userBusinessId,
          feature_key: 'direct_messaging_enhanced'
        });

        if (!jobError) setHasSmartJobMatching(hasJobMatching);
        if (!messagingError) setHasDirectMessaging(hasMessaging);
      } catch (error) {
        console.error('Error checking business features:', error);
        setHasSmartJobMatching(false);
        setHasDirectMessaging(false);
      }
    };

    if (isBusinessOwner && userBusinessId) {
      checkBusinessFeatures();
    }
  }, [userBusinessId, isBusinessOwner]);
  useEffect(() => {
    const checkAccess = async () => {
      if (!user || isBusinessLoading) return;
      if (!hasDashboardAccess) {
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "You do not have permission to access the admin dashboard."
        });
        navigate('/');
      }

      // If user is on the my-business page but doesn't have a business, redirect them
      if (location.pathname === '/admin/my-business' && !isBusinessOwner) {
        navigate('/admin');
      }
    };
    checkAccess();
  }, [user, userRole, isBusinessOwner, isBusinessLoading, hasDashboardAccess, navigate, toast, location.pathname]);
  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };
  const toggleMenu = (menu: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };
  const getUserInitialsForAdmin = () => {
    return getUserInitials(user, userProfile);
  };
  return <SidebarProvider defaultOpen={!isMobile}>
      <div className="dashboard-background flex min-h-screen w-full">
        <Sidebar className="border-r border-border/50 bg-white/80 backdrop-blur-sm">
          <SidebarHeader>
            <div className="flex items-center justify-between px-4 py-6">
              <div className="flex items-center gap-3">
                <Link to="/admin" className="flex items-center space-x-2">
                  {currentCommunity?.logo_url ? (
                    <img
                      src={currentCommunity.logo_url}
                      alt={currentCommunity.name}
                      className="h-8 max-w-32 object-contain community-logo"
                      onError={(e) => {
                        // Hide logo and show fallback text if image fails to load
                        e.currentTarget.style.display = 'none';
                        const fallbackSpan = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fallbackSpan) fallbackSpan.style.display = 'inline';
                      }}
                    />
                  ) : null}
                  <span 
                    className={`text-xl font-bold text-slate-800 whitespace-nowrap ${
                      currentCommunity?.logo_url ? 'hidden' : 'inline'
                    }`}
                  >
                    {currentCommunity?.name}
                  </span>
                </Link>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate('/')} 
                  className="h-8 w-8 hover:bg-primary/10 rounded-lg" 
                  aria-label="Go to Home"
                >
                  <Home className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SidebarHeader>
          
          <SidebarContent className="px-3 bg-transparent">
            <SidebarGroup>
              <SidebarMenu>
                {/* Only show Overview menu item for admin and reviewer roles */}
                {(isAdmin || isReviewer) && (
                  <Link to="/admin" className="admin-sidebar-item">
                    <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
                    <span>Overview</span>
                  </Link>
                )}
              </SidebarMenu>
            </SidebarGroup>

            {/* Only show Your Business section if the user is a business owner */}
            {isBusinessOwner && !isBusinessLoading && <SidebarGroup>
                <div className="text-xs font-bold text-muted-foreground/80 px-3 py-4 uppercase tracking-wider">
                  Your Business
                </div>
                  <Link to="/admin/my-business" className="admin-sidebar-item">
                    <Store className="h-5 w-5 flex-shrink-0" />
                    <span>My Business</span>
                  </Link>
                  
                  {isBusinessApproved ? (
                    <Link to="/admin/my-job-opportunities" className="admin-sidebar-item">
                      <Briefcase className="h-5 w-5 flex-shrink-0" />
                      <span>My Job Opportunities</span>
                      {hasSmartJobMatching === false && !isFreeBusinessType && (
                        <Sparkles className="h-4 w-4 text-purple-600 animate-pulse ml-auto" />
                      )}
                    </Link>
                  ) : (
                    <div className="admin-sidebar-item opacity-50 cursor-not-allowed" title="Business approval required">
                      <Briefcase className="h-5 w-5 flex-shrink-0" />
                      <span>My Job Opportunities</span>
                    </div>
                  )}
                  
                  {isBusinessApproved ? (
                    <Link to="/admin/business-chat" className="admin-sidebar-item">
                      <MessagesSquare className="h-5 w-5 flex-shrink-0" />
                      <span>Customer Chats</span>
                      {hasDirectMessaging === false && !isFreeBusinessType && (
                        <Sparkles className="h-4 w-4 text-purple-600 animate-pulse ml-auto" />
                      )}
                    </Link>
                  ) : (
                    <div className="admin-sidebar-item opacity-50 cursor-not-allowed" title="Business approval required">
                      <MessagesSquare className="h-5 w-5 flex-shrink-0" />
                      <span>Customer Chats</span>
                    </div>
                  )}
                  
                  {/* Show upgrade button or subscription link based on subscription status */}
                  {showUpgradePrompt ? (
                    <div className="px-2 py-1">
                      <UpgradePromptButton variant="sidebar" />
                    </div>
                  ) : showSubscriptionMenu && (
                    <Link to={`/admin/business/subscription?business_id=${userBusinessId}`} className="admin-sidebar-item">
                      <CreditCard className="h-5 w-5 flex-shrink-0" />
                      <span>Subscriptions</span>
                    </Link>
                  )}
              </SidebarGroup>}

            {isAdmin && <>
                <div className="text-xs font-bold text-muted-foreground/80 px-3 py-4 mt-2 uppercase tracking-wider">
                  Administration
                </div>
                
                {/* Businesses - First */}
                <SidebarGroup>
                  <Collapsible open={openMenus.servicesManagement} onOpenChange={() => toggleMenu('servicesManagement')} className="w-full">
                    <CollapsibleTrigger asChild>
                      <div className="admin-sidebar-item justify-between cursor-pointer">
                        <div className="flex items-center gap-3">
                          <Building className="h-5 w-5 flex-shrink-0" />
                          <span>Businesses</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 transition-transform ${openMenus.servicesManagement ? 'transform rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-2 space-y-1 pt-1">
                        {canManageBusiness && (
                          <Link to="/admin/businesses" className="admin-sidebar-item">
                            <Store className="h-5 w-5 flex-shrink-0" />
                            <span>Business List</span>
                          </Link>
                        )}
                        
                        {isAdmin && (
                          <Link to="/admin/reviews" className="admin-sidebar-item">
                            <Star className="h-5 w-5 flex-shrink-0" />
                            <span>Reviews</span>
                          </Link>
                        )}
                        
                        {isAdmin && (
                          <Link to="/admin/discounts" className="admin-sidebar-item">
                            <Tag className="h-5 w-5 flex-shrink-0" />
                            <span>Discounts</span>
                          </Link>
                        )}
                    </CollapsibleContent>
                  </Collapsible>
                </SidebarGroup>

                {/* Users - Second */}
                {canManageUsers && <SidebarGroup>
                  <Collapsible open={openMenus.userManagement} onOpenChange={() => toggleMenu('userManagement')} className="w-full">
                    <CollapsibleTrigger asChild>
                      <div className="admin-sidebar-item justify-between cursor-pointer">
                        <div className="flex items-center gap-3">
                          <Users className="h-5 w-5 flex-shrink-0" />
                          <span>Members</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 transition-transform ${openMenus.userManagement ? 'transform rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-2 space-y-1 pt-1">
                        <Link to="/admin/users" className="admin-sidebar-item">
                          <Users className="h-5 w-5 flex-shrink-0" />
                          <span>Member list</span>
                        </Link>
                        
                        <Link to="/admin/communications" className="admin-sidebar-item">
                          <Mail className="h-5 w-5 flex-shrink-0" />
                          <span>Communications</span>
                        </Link>
                        
                        <Link to="/admin/campaigns" className="admin-sidebar-item">
                          <Target className="h-5 w-5 flex-shrink-0" />
                          <span>Campaigns</span>
                        </Link>
                        
                        <Link to="/admin/email-templates" className="admin-sidebar-item">
                          <LayoutList className="h-5 w-5 flex-shrink-0" />
                          <span>Email Templates</span>
                        </Link>
                    </CollapsibleContent>
                  </Collapsible>
                </SidebarGroup>}

                {/* Site Settings - Third */}
                {canManageSiteSettings && <SidebarGroup>
                    <Collapsible open={openMenus.siteManagement} onOpenChange={() => toggleMenu('siteManagement')} className="w-full">
                      <CollapsibleTrigger asChild>
                        <div className="admin-sidebar-item justify-between cursor-pointer">
                          <div className="flex items-center gap-3">
                            <Settings className="h-5 w-5 flex-shrink-0" />
                            <span>Site Settings</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${openMenus.siteManagement ? 'transform rotate-180' : ''}`} />
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-2 space-y-1 pt-1">
                          <Link to="/admin/community" className="admin-sidebar-item">
                            <Palette className="h-5 w-5 flex-shrink-0" />
                            <span>Community</span>
                          </Link>
                          <Link to="/admin/categories" className="admin-sidebar-item">
                            <Tag className="h-5 w-5 flex-shrink-0" />
                            <span>Categories</span>
                          </Link>
                          <Link to="/admin/badges" className="admin-sidebar-item">
                            <BadgeCheck className="h-5 w-5 flex-shrink-0" />
                            <span>Badges</span>
                          </Link>
                          <Link to="/admin/content-management" className="admin-sidebar-item">
                            <FileText className="h-5 w-5 flex-shrink-0" />
                            <span>Content Management</span>
                          </Link>
                          {canManageTranslations && (
                            <Link to="/admin/translations" className="admin-sidebar-item">
                              <Languages className="h-5 w-5 flex-shrink-0" />
                              <span>Translations</span>
                            </Link>
                          )}
                          {canManageFeatures && (
                            <Link to="/admin/subscription-features" className="admin-sidebar-item">
                              <ListTree className="h-5 w-5 flex-shrink-0" />
                              <span>Features</span>
                            </Link>
                          )}
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarGroup>}

                {/* Integrations - Last */}
                {isAdmin && <SidebarGroup>
                    <Link to="/admin/integrations" className="admin-sidebar-item">
                      <Plug className="h-5 w-5 flex-shrink-0" />
                      <span>Integrations</span>
                    </Link>
                  </SidebarGroup>}
              </>}
          </SidebarContent>
          
          <SidebarFooter className="border-t border-border/50 mt-auto bg-transparent">
            <div className="p-4">
              <Button variant="outline" className="w-full justify-start gap-2 text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200 hover:border-red-300" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
                <span>Log out</span>
              </Button>
            </div>
          </SidebarFooter>
        </Sidebar>
        
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="dashboard-header flex items-center justify-between">
            <div className="flex items-center">
              <SidebarTrigger className="h-10 w-10 p-0 mr-2" />
              
              <div className="text-lg font-semibold ml-2 hidden sm:block text-slate-700">
                {userRole === 'superadmin' ? 'Super Admin' : userRole === 'admin' ? 'Administrator' : userRole === 'reviewer' ? 'Reviewer' : 'Business Owner'} Panel
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Show upgrade button in header for business owners without active subscription */}
              {showUpgradePrompt && (
                <UpgradePromptButton variant="header" />
              )}
              
              <div className="flex items-center gap-2">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium">
                    {user?.user_metadata?.full_name || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {userRole === 'superadmin' ? 'Super Admin' : userRole === 'admin' ? 'Administrator' : userRole === 'reviewer' ? 'Reviewer' : 'Business Owner'}
                  </p>
                </div>
                <Avatar className="h-10 w-10 border-2 border-primary/20 hover:border-primary/40 transition-colors">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-slate-600 text-white font-semibold">
                    {getUserInitialsForAdmin()}
                  </AvatarFallback>
                </Avatar>
              </div>
            </div>
          </header>
          
          <main className="flex-1 overflow-auto p-6 sm:p-8 lg:p-10 bg-transparent">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>;
};

export default AdminLayout;
